import { Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction, PieceAssetSize, PieceAssetType } from '@apiContract';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { getUploadUrl } from '@src/api/piece';

import { ActionComponentProps } from '../../index';
import { DialogSignatureConfirmation } from '../DialogSignatureConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

export const FinishPickUpWithSignature = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const [updatePieces] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const confirmFinishPickUp = async (
    imageBase64: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    try {
      setActionInProgress(true);

      const { pieceAssets } = await getUploadUrl(mailpieces[0].id);

      const { url, key } =
        pieceAssets.find(
          (asset) =>
            asset.pieceAssetType === PieceAssetType.PickupSignature && asset.pieceAssetSize === PieceAssetSize.Large,
        ) || {};

      await apiRequest(uploadImageToS3, { image: imageBase64, uploadUrl: url, contentType: 'image/jpg' }, false, 5);

      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishPickUpWithSignature,
        pickupSignatureKey: key,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      setActionInProgress(false);
    } catch (error) {
      setActionInProgress(false);
    }
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title="Complete with signature"
      onClose={onActionFinish}
      acceptButtonTitle="Capture Signature"
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {mailpieces.length === 1 ? (
            <>Please ask the recipient to sign for this item.</>
          ) : (
            <>
              Please ask the recipient to sign for <strong>{mailpieces.length}</strong> items.
            </>
          )}
        </Typography>
      }
    >
      {({ step, skipNotifyRecipient, comment, notifyMailroom, onClose }) => (
        <DialogSignatureConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          persist={actionInProgress}
          onActionFinish={onClose}
          onConfirm={async (imageBase64) => {
            await confirmFinishPickUp(imageBase64, skipNotifyRecipient, comment, notifyMailroom);
            onClose();
          }}
        />
      )}
    </ActionWithInfo>
  );
};
